import { useQueryClient } from '@tanstack/react-query';
import { type CommunityDetail, createCommunityDetail } from 'models';
import { useQuery } from 'query';
import { useEffect } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: Break circular dependency
import { getProfileQueryKey } from 'features/users/queries/keys';
import { getCommunity } from '../services';
import { getCommunityDataObjectQueryKey, getCommunityQueryKey } from './keys';

export const useCommunityQuery = ({
  communityId,
  invitationToken,
}: {
  communityId?: string;
  invitationToken?: string;
}) => {
  const queryClient = useQueryClient();

  const queryResult = useQuery<CommunityDetail>({
    enabled: Boolean(communityId),
    async queryFn() {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- This will run only if `communityId` is defined
      const { data } = await getCommunity({ id: communityId!, invitationToken });
      return createCommunityDetail(data);
    },
    queryKey: getCommunityQueryKey({ id: communityId, invitationToken }),
    refetchOnMount: invitationToken ? 'always' : undefined,
  });

  // Error handling with invitationToken
  useEffect(() => {
    if (invitationToken && queryResult.isError) {
      void queryClient.invalidateQueries({ queryKey: getCommunityQueryKey({ id: communityId }) });
      void queryClient.invalidateQueries({ queryKey: getCommunityDataObjectQueryKey({ id: communityId }) });
    }
  }, [queryClient, queryResult.isError, invitationToken, communityId]);

  // Success handling with invitationToken
  useEffect(() => {
    if (invitationToken && queryResult.data) {
      queryClient.setQueryData(getCommunityQueryKey({ id: queryResult.data.id }), queryResult.data);
      void queryClient.invalidateQueries({ queryKey: getCommunityDataObjectQueryKey({ id: queryResult.data.id }) });
      void queryClient.invalidateQueries({ queryKey: getProfileQueryKey() });
    }
  }, [queryClient, invitationToken, queryResult.data]);

  return queryResult;
};
